import {PIN_PREFETCH_ZOOM,CATALOG_SLICE} from './map-geometry.mjs';
// Global point data changes only for catalog/filter/live events. Camera queries
// read a small static worker cell for detail pins; they never rebuild point data.
// Between slices the page draws a frame (a hidden tab has no frames: the timer lets the slices through).
const nextFrame=()=>new Promise(resolve=>{const timer=setTimeout(resolve,64);requestAnimationFrame(()=>{clearTimeout(timer);resolve();});});
const viewKey=(filters,hits)=>JSON.stringify([{...filters,plugs:[...filters.plugs||[]].sort()},hits]);
export class MapPipeline {
  constructor(map, changed, failed, worker = new Worker(new URL("/web/dist/map-worker-WQN446BC.js", location.origin),{type:'module'})) {
    this.map=map;this.changed=changed;this.failed=failed;this.worker=worker;
    this.pending=new Map();this.seq=0;this.generation=0;this.ready=false;this.frame=null;this.timer=null;this.last=-Infinity;
    this.coverage=null;this.running=false;this.again=false;this.serial=Promise.resolve();this.ms=0;this.decoded=false;this.waiting=[];
    worker.onmessage=({data:d})=>{this.ms+=d.ms||0;const p=this.pending.get(d.id);this.pending.delete(d.id);if(p)d.error?p.reject(Error(d.error)):p.resolve(d);};
    worker.onerror=()=>{this.ready=false;for(const p of this.pending.values())p.reject(Error('map worker'));this.pending.clear();failed();};
    map.on('move',()=>{if(this.map.getZoom()>=PIN_PREFETCH_ZOOM)this.schedule(true);});
    map.on('moveend',()=>this.schedule());
  }
  call(type,args={},transfer=[]) {return new Promise((resolve,reject)=>{const id=++this.seq;this.pending.set(id,{resolve,reject});this.worker.postMessage({id,type,...args},transfer);});}
  // The decoded catalog comes back in slices (CATALOG_SLICE sites): a single clone of 72k sites would block this thread for a second.
  // `early()` may name the filters/hits of the first view: the worker then builds and sends the dots while the slices still
  // travel, so the map does not wait for the page's copy of the catalog.
  async snapshot(catalog,occupancy){
    const {meta}=await this.call('snapshot',{catalog,occupancy},[catalog,occupancy]),sites=[];
    sites.version=meta.version;sites.seq=meta.seq;this.catalog=sites;this.catalogReusable=true;
    // The worker holds the decoded catalog now: hits and camera of a search can be asked for before anything else is queued.
    this.decoded=true;for(const run of this.waiting.splice(0))run();this.onDecoded?.();
    const want=!this.ready&&this.early?.();
    if(want){const promise=this.load(sites,want.filters,want.hits);promise.catch(()=>{});this.preloaded={sites,key:viewKey(want.filters,want.hits),promise};}
    else this.preloaded=null;
    for(let from=0;from<meta.sites;from+=CATALOG_SLICE){
      sites.push(...(await this.call('catalogChunk',{from,epoch:meta.epoch})).sites);
      await nextFrame();
    }
    return sites;
  }
  // The early dots, if they were built for exactly this catalog, filter and hit list (then the first full load is already done).
  takePreloaded(sites,filters,hits){
    const p=this.preloaded;this.preloaded=null;
    return p&&p.sites===sites&&p.key===viewKey(filters,hits)?p.promise:null;
  }
  // Live resync: statuses of a fresh occupancy.bin as a Uint8Array, decoded off the main thread.
  async occupancy(buffer){return (await this.call('occupancy',{buffer},[buffer])).occupancy;}
  async decodePrices(buffer){return (await this.call('decodePrices',{buffer},[buffer])).table;}
  // Ranked hit features for `ids` from the worker's catalog (the page's slow copy is not needed); camera for a search inside `view`.
  // Both wait for the first decoded catalog and are posted in the order asked, ahead of the first model build.
  peek(ids,filters){return this.whenDecoded(()=>this.call('peek',{ids,filters})).then(d=>d.features);}
  searchCamera(point,ids,filters,view,cap){return this.whenDecoded(()=>this.call('frame',{point,ids,filters,view,cap})).then(d=>({camera:d.camera,coords:d.coords}));}
  whenDecoded(request){
    if(this.decoded)return request();
    return new Promise((resolve,reject)=>this.waiting.push(()=>{try{resolve(request());}catch(error){reject(error);}}));
  }
  // Diff order is significant: a later filter diff is relative to the previous
  // model state, even when multiple UI actions arrive before rendering settles.
  update(type,args) {
    this.generation++;this.coverage=null;
    const job=this.serial.then(async()=>{
      const d=await this.call(type,args);this.ready=true;
      await this.changed(d);await this.rest(d);this.schedule();return d;
    });
    this.serial=job.catch(()=>this.failed());return job;
  }
  // Slices of a big reply, applied in order. A newer reply replaces them in the worker; its own slices then follow.
  async rest(d){
    for(let index=1;d.more&&index<d.more.count;index++){
      const part=await this.call('part',{epoch:d.more.epoch,index});
      if(part.stale)return;
      await this.changed(part);await nextFrame();
    }
  }
  invalidate(){this.generation++;this.ready=false;this.coverage=null;}
  load(points,filters,hits){this.preloaded=null;this.loadedCatalog=points;return this.update('load',{points:points===this.catalog&&this.catalogReusable?undefined:points,filters,hits,gpu:this.gpu});}
  hits(hits){return this.update('hits',{hits});}
  filter(filters){return this.update('filter',{filters});}
  delta(updates){
    // Prices can delay installation of an initial/replacement snapshot. Live has
    // already updated its UI catalog; do not patch a missing or older model.
    if(this.catalog&&this.loadedCatalog!==this.catalog){this.catalogReusable=false;return Promise.resolve();}
    return this.update('delta',{updates});
  }
  prices(updates){
    if(this.catalog&&this.loadedCatalog!==this.catalog){this.catalogReusable=false;return Promise.resolve();}
    return this.update('prices',{updates});
  }
  restore(){
    this.coverage=null;
    const job=this.serial.then(async()=>{if(!this.ready)return;const d=await this.call('restore');await this.changed(d);await this.rest(d);this.schedule();});
    this.serial=job.catch(()=>this.failed());return this.serial;
  }
  camera(){const box=this.map.getContainer?.();return [this.map.getZoom(),this.map.getBearing?.()||0,this.map.getPitch?.()||0,this.map.getRoll?.()||0,
    this.map.getProjection?.()?.type||'globe',box?.clientWidth,box?.clientHeight,JSON.stringify(this.map.getPadding?.()||{}),!!this.map.getTerrain?.()];}
  covered(){
    const c=this.coverage,camera=this.camera();
    // Native globe is fully Mercator by z12. Only near, flat translations
    // preserve the padded pin collection and its badge collision geometry.
    if(!c||c.generation!==this.generation||camera[0]<PIN_PREFETCH_ZOOM||camera[2]||camera[3]||camera[8]||!['mercator','globe'].includes(camera[4])||camera.some((v,i)=>v!==c.camera[i]))return false;
    const b=this.map.getBounds(),[w,s,e,n]=c.bounds;
    return b.getWest()>=w&&b.getEast()<=e&&b.getSouth()>=s&&b.getNorth()<=n;
  }
  schedule(moving=false) {
    if(!this.ready)return;
    if(moving && performance.now()-this.last<150){if(!this.timer)this.timer=setTimeout(()=>{this.timer=null;this.schedule(true);},150-(performance.now()-this.last));return;}
    clearTimeout(this.timer);this.timer=null;
    if(moving&&this.covered()){this.last=performance.now();return;}
    if(this.frame!==null)return;
    this.frame=requestAnimationFrame(()=>{this.frame=null;this.query();});
  }
  async query(){
    if(!this.ready)return;
    if(this.running){this.again=true;return;}
    this.running=true;this.last=performance.now();const generation=this.generation;
    try{
      // At globe distances only the handful of ranked hit pins is queried;
      // never interpret sky-corner viewport bounds as catalog visibility.
      const camera=this.camera(),zoom=camera[0],b=zoom>=PIN_PREFETCH_ZOOM?this.map.getBounds():null;
      const bounds=b?[b.getWest(),b.getSouth(),b.getEast(),b.getNorth()]:[-180,-90,180,90];
      // Small viewports otherwise retain many off-screen symbols in native
      // tiles; their placement/atlas cost exceeds the saved worker queries.
      if(b){const pad=this.map.isZooming?.()||camera[5]<600? .15:1,x=(bounds[2]-bounds[0])*pad,y=(bounds[3]-bounds[1])*pad;bounds[0]-=x;bounds[2]+=x;bounds[1]-=y;bounds[3]+=y;}
      const d=await this.call('query',{bounds,zoom});
      if(generation===this.generation && this.ready){await this.changed({pins:d.features});this.coverage=b&&generation===this.generation?{bounds,camera,generation}:null;}
    }catch{this.coverage=null;this.failed();}
    finally{this.running=false;if(this.again){this.again=false;this.schedule();}}
  }
}
